import { useState } from 'preact/hooks';
import { ChipCount } from '../components/ChipCount';
import type { PrSummary } from '../../shared/pr/api';
import { isOpenForRequester } from '../../shared/pr/progress';
import { TERMINAL } from '../../shared/rules/statuses';
import { RequestRow } from '../components/RequestRow';
import { useI18n, type MessageKey } from '../i18n';
import { useMyRequests } from '../lib/requests';
import { StatusGuide } from '../components/StatusGuide';
import { LoadProblem } from './Home';

// My requests (screen 03): only the signed-in person's own requests; the broker filters them.

const FILTERS = {
  all: () => true,
  open: (r: PrSummary) => isOpenForRequester(r.status),
  drafts: (r: PrSummary) => r.status === 'Draft',
  closed: (r: PrSummary) => TERMINAL.includes(r.status),
} as const;
type Filter = keyof typeof FILTERS;

export function MyRequestsPage() {
  const { t } = useI18n();
  const state = useMyRequests();
  const [filter, setFilter] = useState<Filter>('all');
  const requests = state.kind === 'ok' ? state.requests : [];
  const shown = requests.filter(FILTERS[filter]);

  return (
    <div class="stack">
      <div class="actions">
        <a class="button button--primary" href="#/requests/new">
          + {t('home.newRequest')}
        </a>
      </div>
      <section class="card" aria-labelledby="my-title">
        <h2 id="my-title" class="visually-hidden">
          {t('nav.myRequests')}
        </h2>
        <div class="admin-tabs" role="group" aria-label={t('req.filter')}>
          {(Object.keys(FILTERS) as Filter[]).map((f) => (
            <button
              key={f}
              type="button"
              class="chip"
              aria-pressed={f === filter}
              onClick={() => setFilter(f)}
            >
              {t(`req.filter.${f}` as MessageKey)}
              <ChipCount n={requests.filter(FILTERS[f]).length} />
            </button>
          ))}
        </div>
        {state.kind === 'loading' && <p aria-busy="true">{t('req.loading')}</p>}
        {state.kind === 'error' && <LoadProblem state={state} />}
        {state.kind === 'ok' &&
          (shown.length === 0 ? (
            <p>{t('req.none')}</p>
          ) : (
            <ul class="req-list">
              {shown.map((r) => (
                <RequestRow key={r.prNo} pr={r} />
              ))}
            </ul>
          ))}
      </section>
      <StatusGuide staff={false} />
    </div>
  );
}
